import Sidebar from '../components/Sidebar';

function HowWeWork() {
  const strategicPillars = [
    {
      title: 'Platform Engineering',
      description: 'Modern, scalable cloud infrastructure',
      icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
      details: [
        'Cloud-native architecture (AWS/GCP)',
        'Containerization with Docker',
        'Infrastructure as Code (Terraform/CloudFormation)',
        'Microservices where appropriate, monolith where sensible',
        'API-first design',
      ],
    },
    {
      title: 'Security & Reliability',
      description: 'Secure by design, built to last',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      details: [
        'Security-first mindset in all code',
        'Automated security scanning',
        '99.9% uptime SLAs',
        'Comprehensive monitoring and alerting',
        'Disaster recovery plans',
        'Regular security audits',
      ],
    },
    {
      title: 'Automation First',
      description: 'Automate everything automatable',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      details: [
        'CI/CD pipelines for all projects',
        'Automated testing (unit, integration, E2E)',
        'Infrastructure as code',
        'Automated deployments',
        'Self-healing systems',
      ],
    },
    {
      title: 'Open Source',
      description: 'Leverage and contribute to the community',
      icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
      details: [
        'Prefer open source solutions',
        'Contribute back to projects we use',
        'Transparent licensing',
        'Avoid vendor lock-in',
        'Build reusable components',
      ],
    },
    {
      title: 'Engineering Experience',
      description: 'Code quality and developer happiness',
      icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
      details: [
        'Clean, maintainable code',
        'Comprehensive documentation',
        'Code reviews on every PR',
        'Pair programming for complex features',
        'Investment in developer tools',
      ],
    },
  ];

  const principles = [
    {
      title: 'Build Confidence',
      description: 'Proactive communication and transparency with clients',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      practices: [
        'Weekly client updates (every Friday)',
        'Demo-driven development',
        'Transparent roadmaps and timelines',
        'Honest about challenges and delays',
        'Status dashboards clients can check anytime',
      ],
    },
    {
      title: 'Write to Think',
      description: 'Documentation-first approach to decision making',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      practices: [
        'Architecture Decision Records (ADRs) for major decisions',
        'Design docs before implementation',
        'RFC process for significant changes',
        'README-driven development',
        'Inline code documentation',
      ],
    },
    {
      title: 'Meetings Used Sparingly, With Purpose',
      description: 'Respect everyone\'s time and focus',
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      practices: [
        'Agenda required for all meetings',
        'Time-boxed discussions',
        'Decision-focused, not status updates',
        'Async alternatives preferred (Slack, Loom)',
        'No meeting blocks for deep work',
      ],
    },
    {
      title: 'Async by Default',
      description: 'Enable distributed, flexible collaboration',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      practices: [
        'Written communication in Slack',
        'Documented decisions everyone can reference',
        'Timezone-friendly collaboration',
        'Recorded demos for async viewing',
        'Clear expectations on response times',
      ],
    },
  ];

  const sprintEvents = [
    {
      day: 'Monday Week 1',
      title: 'Sprint Planning',
      description: 'Review and refine backlog, estimate stories, commit to sprint goal',
      duration: '1-2 hours',
    },
    {
      day: 'Daily',
      title: 'Async Standups',
      description: 'Slack thread each morning: Yesterday / Today / Blockers',
      duration: 'Ongoing',
    },
    {
      day: 'Wednesday Week 1',
      title: 'Mid-Sprint Check-in',
      description: 'Quick sync on progress, address blockers, adjust scope if needed',
      duration: '30 minutes',
    },
    {
      day: 'Friday Week 2',
      title: 'Sprint Review',
      description: 'Demo completed work to stakeholders, gather feedback',
      duration: '1 hour',
    },
    {
      day: 'Friday Week 2',
      title: 'Sprint Retrospective',
      description: 'What went well? What could improve? Action items for next sprint',
      duration: '45 minutes',
    },
  ];

  const tools = [
    {
      category: 'Version Control',
      items: ['Git for all code', 'Feature branch workflow', 'PR reviews required (2 approvals)', 'Main branch protected'],
    },
    {
      category: 'Project Management',
      items: ['Linear/Jira for story tracking', 'Sprint boards', 'Burndown charts', 'Client-visible roadmaps'],
    },
    {
      category: 'Communication',
      items: ['Slack (primary async)', 'Zoom (when necessary)', 'Loom (recorded demos)', 'Email (external only)'],
    },
    {
      category: 'Documentation',
      items: ['Notion for company knowledge', 'README files in every repo', 'API documentation (Swagger/OpenAPI)', 'Runbooks for operations'],
    },
    {
      category: 'Code Quality',
      items: ['ESLint/Prettier for consistency', 'Automated test suites', 'Code coverage targets (80%+)', 'SonarQube for static analysis'],
    },
  ];

  const resources = [
    { title: 'ADR Template', description: 'Template for Architecture Decision Records' },
    { title: 'Sprint Planning Template', description: 'Guide for effective sprint planning sessions' },
    { title: 'Client Update Template', description: 'Weekly client communication format' },
    { title: 'Retrospective Framework', description: 'Structure for productive retrospectives' },
    { title: 'Code Review Guidelines', description: 'Standards for reviewing code' },
    { title: 'Onboarding Checklist', description: 'New team member onboarding steps' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <Sidebar />

      {/* Main Content */}
      <div className="ml-64 p-8">
        {/* Header */}
        <header className="mb-8 fade-in-up">
          <h1 className="text-4xl font-bold text-display mb-2" style={{ color: 'var(--color-text-primary)' }}>
            How We <span className="gradient-text">Work</span>
          </h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Our engineering strategy, principles, and methodology
          </p>
        </header>

        {/* Strategic Pillars */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Our Strategic Foundation
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            Five pillars guide every technical decision we make
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {strategicPillars.map((pillar, i) => (
              <div key={i} className="card fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(59, 113, 170, 0.1)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={pillar.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      {pillar.title}
                    </h3>
                    <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                      {pillar.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {pillar.details.map((detail, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Core Principles */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            How We Operate
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            These principles shape our daily work
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, i) => (
              <div key={i} className="card fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(59, 113, 170, 0.1)' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={principle.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      {principle.title}
                    </h3>
                    <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                      {principle.description}
                    </p>
                  </div>
                </div>
                <div className="pl-16">
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>How we do it:</p>
                  <ul className="space-y-2">
                    {principle.practices.map((practice, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className="text-xs mt-1" style={{ color: 'var(--color-primary)' }}>•</span>
                        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{practice}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Agile Methodology */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            How We Execute: Agile Sprints
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            We use 2-week sprint cycles for predictable, iterative delivery
          </p>

          <div className="card mb-6">
            <div className="border-l-4 pl-4 mb-6" style={{ borderColor: 'var(--color-primary)' }}>
              <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                2-Week Sprint Cycle
              </h3>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Each sprint includes planning, daily async standups, mid-sprint check-in, review, and retrospective
              </p>
            </div>

            <div className="space-y-4">
              {sprintEvents.map((event, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-lg" style={{ backgroundColor: i % 2 === 0 ? 'rgba(59, 113, 170, 0.05)' : 'transparent' }}>
                  <div className="flex-shrink-0 w-32">
                    <p className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {event.day}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {event.duration}
                    </p>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      {event.title}
                    </h4>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tools & Practices */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Our Tool Stack
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {tools.map((toolCategory, i) => (
              <div key={i} className="card fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                  {toolCategory.category}
                </h3>
                <ul className="space-y-2">
                  {toolCategory.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section>
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
            Resources & Templates
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--color-text-secondary)' }}>
            Templates and guides to help you work the BrightWings way
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {resources.map((resource, i) => (
              <div key={i} className="card hover:shadow-lg transition-shadow cursor-pointer fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      {resource.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {resource.description}
                    </p>
                  </div>
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default HowWeWork;
